import React, { useEffect, useState } from "react"
import ReactSelect from "react-select"
import { motion } from "framer-motion"
import { DEFAULT_SELECT_STYLES } from "./../../../utils/forms"
import {
  GroupedOptionsType,
  OptionType,
  OptionsType,
  SelectPropsType,
} from "./types"

/**
 * Transforms the options into the allowed format, which could be either
 *
 *  - Normal options:
 *    [
 *      {label: "option 1", value: "1"},
 *      {label: "option 2", value: "2"},
 *      ...
 *    ]
 *
 *  - Grouped options:
 *    [
 *      {label: "Group 1", options: [{label: "option 1", value: "1"}, {label: "option 2", value: "2"}, ..]},
 *      {label: "Group 2", options: [{label: "option 3", value: "3"}, {label: "option 4", value: "4"}, ..]},
 *      ...
 *    ]
 */
const formatOptions = (
  options: OptionsType
): (OptionType | GroupedOptionsType)[] => {
  if (Array.isArray(options)) {
    return options
  }
  return typeof options[Object.keys(options)[0]] === "string"
    ? Object.entries(options).reduce((acc, [value, label]) => {
        // @ts-ignore
        acc.push({ label: label, value: value })
        return acc
      }, [] as OptionType[])
    : Object.entries(options).reduce((acc, [label, values]) => {
        acc.push({
          label: label,
          options: Object.entries(values).reduce(
            (opts, [k, v]) => [...opts, { label: v, value: k }],
            [] as OptionType[]
          ),
        })
        return acc
      }, [] as GroupedOptionsType[])
}

const getValue = (
  options: (OptionType | GroupedOptionsType)[],
  value: string
): any => {
  for (let option of options) {
    if (option.hasOwnProperty("options")) {
      for (let opt of (option as GroupedOptionsType).options) {
        if (value === opt.value) {
          return opt
        }
      }
      return option
    } else if (value === (option as OptionType).value) {
      return option
    }
  }
  return null
}

const Select: React.FC<SelectPropsType> = ({
  value,
  styles,
  options,
  label,
  ...props
}) => {
  const [show, setShow] = useState<boolean>(false)
  const opts = formatOptions(options) as any

  useEffect(() => setShow(true), [])

  return (
    show && (
      <motion.label
        className={"label-" + (props.className ? props.className : props.name)}
      >
        {!!label && <span>{label}</span>}
        <ReactSelect
          options={opts}
          value={typeof value === "string" ? getValue(opts, value) : value}
          styles={styles ? styles : DEFAULT_SELECT_STYLES}
          {...props}
        />
      </motion.label>
    )
  )
}

export default Select
